<script setup>
    import { computed, ref, inject, defineEmits } from "vue";
    import { useMutation, useQueryClient } from "@tanstack/vue-query";
    import { ElButton, ElInput, ElSwitch } from "element-plus";
    import { useQuery } from "@tanstack/vue-query";
    import iPipelineLogo from "../../assets/images/iPipelineLogo.png";
    import axios from "axios";

    const userService = inject('UserService');

    const queryClient = useQueryClient();

    const isAdmin = computed(() => !!userService.getUserDetails()?.isAdminUser);

    const { data: currentIntegration, isLoading: isLoadingCurrent } = useQuery({
        queryKey: ["ipipeline-integration"],
        queryFn: () =>
            axios("api/Integration/GetIPipelineIntegration").then((res) => res.data),
    });

    const { mutate: deleteUserIntegration, isLoading: isDeleting } = useMutation({
        mutationFn: () =>
            axios.delete("api/Integration/DeleteUserIntegration/IPIPELINE"),
        onSuccess: () => {
            iPipelineCredentials.value.userName = "";
            iPipelineCredentials.value.password = "";
            queryClient.invalidateQueries(["ipipeline-integration"]);
            queryClient.invalidateQueries(["user-integrations"]);
            userService.update();
        },
    });

    const iPipelineCredentials = ref({
        userName: "",
        password: "",
        isExt: false,
    });

    const emit = defineEmits(["saveCredentials"]);

    const { mutate: saveCredentials, isLoading: isSaving, isError } = useMutation({
        mutationFn: () =>
            axios
                .post(
                    "api/Integration/SaveIPipelineCredentials",
                    iPipelineCredentials.value
                ),
        onSuccess: async () => {
            queryClient.invalidateQueries(["ipipeline-integration"]);
            queryClient.invalidateQueries(["user-integrations"]);
            await userService.update();
            emit("saveCredentials","IPIPELINE");
        }
    });

    const isLoading = computed(
        () => isSaving.value || isLoadingCurrent.value || isDeleting.value
    );
    const disabled = computed(() => !!currentIntegration.value || isLoading.value);
</script>

<template>
    <div class="integration-section" style="background-color: #d5e7f7;">
        <div class="right-section">
            <div class="description">
                <p class="mb-2 font-same">
                    To ensure that the prices and quotes we obtain from iPipeline match the products and pricing available to you, please
                    provide us with your <b>WebID</b> and <b>Password</b>.
                </p>
                <p class="mb-2 font-same">
                    <b>Important:</b> Your WebID and Password are <b>not</b> the same as the login + credentials (email and password) you use to access iPipeline directly.
                </p>
                <p class="mb-2 font-style">

                    If you're unsure of your WebID and Password, you can follow our tutorial
                    <a href="https://help.ipipeline.uk.com/knowledgebase/how-do-i-recover-my-web-id-and-password-on-ipipelines-site/"
                       target="_blank" class="text-blue-600"> HERE</a>.
                </p>
                <p class="mb-2 font-style">
                    You only need to provide these details once, and we will not ask for them again.
                </p>
            </div>
            <div class="form-section">
                <div class="form-card">
                    <div class="image-logo">
                        <img :src="iPipelineLogo" alt="iPipeline" />
                    </div>
                    <div class="form-group">
                        <div class="form-row">
                            <label for="WebID">WebID</label>
                            <a target="_blank"
                               href="https://protectionguru.co.uk/protectiongurupro/how-to-use-protectiongurupro/recover-ipipeline-webid-password/" class="text-blue-600">What is this?</a>
                        </div>
                        <ElInput v-if="!!currentIntegration" id="WebID" v-model="currentIntegration.userName" disabled />
                        <ElInput v-else id="WebID" v-model="iPipelineCredentials.userName" :min-length="1" type="text" autofocus :class="{ error : isError}" />
                    </div>

                    <div class="form-group">
                        <div class="form-row">
                            <label for="Password">Password</label>
                            <a target="_blank"
                               href="https://protectionguru.co.uk/protectiongurupro/how-to-use-protectiongurupro/recover-ipipeline-webid-password/#Password" class="text-blue-600">How do I get this?</a>
                        </div>
                        <ElInput v-if="!!currentIntegration" id="Password" disabled value="xxxxxxxxxxxxx" type="password" />
                        <ElInput v-else id="Password" v-model="iPipelineCredentials.password" :min-length="1" type="password" />
                        <span v-if="isError" class="errorText">Sorry, we are unable to match your WebID to an IPipeLine account, please check and try again.</span>
                    </div>

                    <div class="form-group" v-if="isAdmin">
                        <div class="ext">
                            <label for="isEXT">EXT Environment <b>(Admin only)</b></label>
                            <ElSwitch id="isEXT" v-model="iPipelineCredentials.isExt" :disabled="!!currentIntegration" />
                        </div>
                    </div>

                    <ElButton v-if="!!currentIntegration" type="danger" @click="deleteUserIntegration" :loading="isLoading">
                        Unlink account
                    </ElButton>

                    <ElButton v-else type="primary" @click="saveCredentials" :loading="isLoading" :disabled="disabled">
                        Link account
                    </ElButton>
                </div>

                <div class="help-links">
                    <p>
                        <span>Can’t remember your WebID or password?</span>
                        <a target="_blank" class="text-blue-600"
                           href="https://protectionguru.co.uk/protectiongurupro/how-to-use-protectiongurupro/recover-ipipeline-webid-password/">
                            Click Here
                        </a>
                    </p>
                    <p>
                        <span>Don't have an iPipeline account? </span>
                        <a target="_blank" href="https://www.ipipeline.uk.com/sign-up/" class="text-blue-600">Click Here</a>
                        <span> to set up an account, it's FREE!</span>
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .errorText {
        color: red;
        font-size: small;
    }

    .form-section {
        width: 350px;
        background-color: #ffffff;
        padding: 10px;
    }

    .form-group {
        margin-bottom: 1rem;
    }

    .form-card {
        background-color: #ffffff;
        padding: 15px;
        box-shadow: 0 2px 12px 0 rgba(0,0,0,.1);
    }

    .form-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.25rem;
    }
        .form-row label {
            font-size: 0.875rem;
        }
    .right-section {
        flex: 1;
        display: flex;
    }

        .right-section .description {
            flex: 1;
            padding-right: 2rem;
            font-size: 0.875rem;
        }

    .help-links {
        margin-top: 1rem;
        font-size: 0.75rem;
    }

        .help-links p {
            margin-bottom: 0.25rem;
            font-weight: 600;
        }

    .image-logo {
        background: white;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        margin-bottom: 10px;
    }

        .image-logo img {
            max-width: 140px;
            max-height: 30px;
            object-fit: cover;
            margin-bottom: 5px;
        }

    .ext {
        display: flex;
        width: 100%;
        justify-content: space-between;
        align-items: center;
    }
</style>

<style>
    .error .el-input__wrapper {
        border: 1px solid red;
    }
</style>
